import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { DEFAULT_WEEKDAYS } from "@msuite/utils/date";
import { type ProjectSchemaForm, projectSchema } from "@msuite/utils/schemas";
import type { Tables } from "@msuite/utils/types";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ProjectSearch } from "@/components/projects";
import { ProjectForm, useProjectMemberController } from "@/forms/project-form";
import { ProjectTicketsForm } from "@/forms/project-form/project-tickets-form";
import { useProjectTicketsController } from "@/forms/project-form/use-project-tickets-controller";
import { client } from "@/hono";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { AddTicketModal } from "../add-ticket-modal";

/** Props Interface */
export interface AddProjectModalProps extends ModalProps {
	onComplete: (project: Tables<"projects">) => Promise<void> | void;
	defaultValues?: Partial<ProjectSchemaForm>;
}

type View = "general" | "tickets";

export function AddProjectModal({
	onComplete,
	defaultValues = {},
	...props
}: AddProjectModalProps) {
	/** State */
	const [currentView, setCurrentView] = useState<View>("general");

	/** Hooks */
	const addTicketModal = useDisclosure();
	const { t } = useTranslation();
	// The site does not exist yet, so there is no id for the rule to bind to — the category is
	// what `createIsDenied` asks for a null project, and the route's gate asks the same thing.
	const { createIsDenied } = useTicketRules();
	const projectMembers = useProjectMemberController({
		projectId: null,
	});
	const projectTickets = useProjectTicketsController({
		projectId: null,
	});
	const qc = useQueryClient();
	const methods = useForm({
		// A site works Monday to Friday until somebody says otherwise, which is what every project
		// created before the column existed carries too.
		defaultValues: { weekdays: DEFAULT_WEEKDAYS, ...defaultValues } as ProjectSchemaForm,
		resolver: resolver(projectSchema),
		reValidateMode: "onSubmit",
	});

	/** Functions */
	async function handleOnSubmit(json: ProjectSchemaForm) {
		try {
			if (json.id) {
				await projectTickets.handleOnSubmit(json.id);
				await handleOnPatch(json);
			} else {
				const result = await handleOnPost(json);
				if (!result) throw new Error("Failed to create project");
				await projectTickets.handleOnSubmit(result.id);
			}
			await qc.invalidateQueries({
				predicate: invalidate(["projects"]),
			});
			props.onClose();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnReject() {
		toast.error(t("general.fill_in_required_fields"));
	}

	async function handleOnPost(json: ProjectSchemaForm) {
		try {
			const response = await client.projects.index.$post({
				json,
			});
			const data = await responseHandler(response);
			if (data.project) {
				await projectMembers.handleOnSubmit(data.project.id);
				await onComplete(data.project);
				return data.project;
			}
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnPatch(json: ProjectSchemaForm & { id?: string }) {
		try {
			if (!json.id) throw new Error("Project ID is required");
			const response = await client.projects[":projectId"].$patch({
				param: { projectId: json.id },
				json,
			});
			const data = await responseHandler(response);
			if (!data.project?.id) throw new Error("Project not found");
			await projectMembers.handleOnSubmit(data.project.id);
			await onComplete(data.project);
			return data.project;
		} catch (error) {
			toast.error(t("general.something_went_wrong"));
			console.error(error);
		}
	}

	async function handleOnSelectSearch(projectId: string | null) {
		try {
			if (!projectId) return;
			const response = await client.projects[":projectId"].$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			if (!data.project?.id) throw new Error("Project not found");
			await projectMembers.handleOnReset(data.project.id);
			methods.reset(data.project);
		} catch (error) {
			toast.error(t("general.something_went_wrong"));
			console.error(error);
		}
	}

	/** Computed */
	// Picking a site through the search fills the form with that site, id included, and the
	// submit then updates it rather than creating one — so the dialog says so.
	const existingProjectId = methods.watch("id");

	/** Render */
	return (
		<React.Fragment>
			<FormProvider {...methods}>
				<Modal
					isOpen={props.isOpen}
					onClose={props.onClose}
					title={existingProjectId ? t("projects.edit_project") : t("projects.add_project")}
					onSubmit={methods.handleSubmit(handleOnSubmit, handleOnReject)}
					data-cy="project-form"
					width={currentView === "tickets" ? "700px" : undefined}
				>
					<Modal.Body className="p-0">
						<div className="px-6 pt-4 h-stack justify-between">
							<SegmentedControl
								value={currentView}
								onChange={setCurrentView}
								items={
									[
										{
											label: t("projects.project"),
											value: "general",
											cyId: "project-modal-general-tab",
										},
										{
											label: t("projects.tickets"),
											value: "tickets",
											cyId: "project-modal-tickets-tab",
										},
									] as const
								}
							/>
							{currentView === "tickets" && !createIsDenied(null) && (
								<Button
									icon={<Icon name="Plus" />}
									onClick={addTicketModal.onOpen}
									data-cy="project-modal-add-ticket-button"
								>
									{t("projects.add_ticket")}
								</Button>
							)}
						</div>
						{currentView === "general" && (
							<div className="v-stack gap-4 overflow-y-auto overflow-x-hidden pb-6 px-6 pt-0">
								<ProjectSearch onChange={handleOnSelectSearch} />
								<ProjectForm projectMemberController={projectMembers.controller} />
							</div>
						)}
						{currentView === "tickets" && (
							<div className="v-stack gap-4 overflow-y-auto overflow-x-hidden pb-6 px-6 pt-0">
								<ProjectTicketsForm
									projectTicketsController={projectTickets.controller}
									disclosure={addTicketModal}
									projectId={null}
								/>
							</div>
						)}
					</Modal.Body>
					<Modal.FooterForm submitLabel={existingProjectId ? t("general.save") : undefined} />
				</Modal>
			</FormProvider>
			{addTicketModal.isOpen && (
				<AddTicketModal
					onClose={addTicketModal.onClose}
					isOpen={addTicketModal.isOpen}
					projectId={null}
					withProjectSearch={false}
					onComplete={async (ticket, uploadResults) => {
						projectTickets.controller.addNewTicket({
							...ticket,
							uploadResults,
						});
					}}
				/>
			)}
		</React.Fragment>
	);
}
